'use client';
import {
  Box,
  ButtonBase,
  ButtonProps,
  darken,
  useTheme,
  CircularProgress,
  Typography,
} from '@mui/material';
import Optional from '@jd/ui/components/Optional';

const buttonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'uppercase',
  px: 3,
  py: 1.5,
  borderRadius: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
};

const AnimatedDepthButton = ({ children, loading, ...rest }: ButtonProps) => {
  const theme = useTheme();
  const { loadingPosition, ...baseButtonProps } = rest;

  // Always use darken for the shadow to create the 3D edge
  const shadowColor = darken(theme.palette.primary.main, 0.3);

  return (
    <ButtonBase
      {...baseButtonProps}
      disabled={loading || baseButtonProps.disabled}
      sx={{
        ...buttonSx,
        position: 'relative',
        boxShadow: `0px 4px 0px ${shadowColor}`,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        marginBottom: '4px', // Reserve space for the shadow/press so layout doesn't shift
        ':hover': {
          textDecoration: 'none !important',
          transform: loading ? 'none' : 'translateY(2px)',
          boxShadow: loading
            ? `0px 4px 0px ${shadowColor}`
            : `0px 2px 0px ${shadowColor}`,
        },
        ':active': {
          transform: loading ? 'none' : 'translateY(4px)',
          boxShadow: `0px 0px 0px ${shadowColor}`,
        },
        ...baseButtonProps.sx,
      }}
    >
      <Typography
        variant={'button'}
        sx={{
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.2s ease',
          fontSize: 15,
          lineHeight: 1.75,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>

      <Optional condition={!!loading}>
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
        </Box>
      </Optional>
    </ButtonBase>
  );
};

export default AnimatedDepthButton;
